import { LoadableModelEntityReference } from '@upright/common/model/output/scores/model-entity-data/types/types-entity-with-result-common';
import { fetchAndValidateOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { Options, optionsToString } from '@upright/ui/api/common/util';
import {
  companyLikeEntityTypes,
  ModelEntityReference,
  productLikeEntityTypes,
} from '@upright/common/model/input/types';
import {
  FetchModelEntityDataInBulkOptions,
  FetchSingleModelEntityDataOptions,
  GetModelEntityDataInBulkPayload,
  zGetModelEntityDataInBulkResponse,
  zModelEntityWithScores,
} from '@upright/common/model/output/scores/model-entity-data/api-types';
import { ModelEntityWithScores } from '@upright/common/model/output/scores/model-entity-data/types';

export async function fetchSingleModelEntityData(
  ref: ModelEntityReference,
  releaseOrVersion: string,
  options?: FetchSingleModelEntityDataOptions,
) {
  return await fetchAndValidateOrThrow<ModelEntityWithScores>({
    path: `/scores/${releaseOrVersion}/entity/${ref.type}/${ref.id}${optionsToString(options as Options)}`,
    schema: zModelEntityWithScores,
  });
}

export async function fetchModelEntityDataInBulkOrThrow({
  payload,
  options,
}: {
  payload: GetModelEntityDataInBulkPayload;
  options?: FetchModelEntityDataInBulkOptions;
}) {
  return await fetchAndValidateOrThrow<ModelEntityWithScores[]>({
    path: `/scores/bulk${optionsToString(options as Options)}`,
    schema: zGetModelEntityDataInBulkResponse,
    method: 'POST',
    bodyContent: pruneBulkLoadPayload(payload),
  });
}

/**
 * Avoid including a version or release in the bulk query if not needed. Reason: the user may not
 * have access to product-like entities of a version currently selected in the UI. The backend will
 * consider a query with that version as unauthorized, even if no product-like data is queried for.
 */
function pruneBulkLoadPayload(payload: GetModelEntityDataInBulkPayload): GetModelEntityDataInBulkPayload {
  const { entities, version, release } = payload;
  return {
    entities,
    version: entitiesNeedVersion(entities) ? version : undefined,
    release: entitiesNeedRelease(entities) ? release : undefined,
  };
}

function entitiesNeedVersion(refs: LoadableModelEntityReference[]) {
  const types: readonly unknown[] = productLikeEntityTypes;
  return refs.some(ref => types.includes(ref.type));
}

function entitiesNeedRelease(refs: LoadableModelEntityReference[]) {
  const types: readonly unknown[] = companyLikeEntityTypes;
  return refs.some(ref => types.includes(ref.type));
}
